// SPDX-FileCopyrightText: 2025 Land Baden-Württemberg <InnoLab@stm.bwl.de>
//
// SPDX-License-Identifier: MPL-2.0

/**
 * Manages Web-Worker lifecycle for parser termination
 */

let messageIdCounter = 0;

/**
 * Short usage explanation for parameters:
 *
 * @param {string} workerUrl - URL of the worker script (from import)
 * @param {Object} message - Message to send to worker
 * @param {number} timeoutMs - Timeout in milliseconds
 * @returns {Promise<Object>} Worker response
 * @throws {Error} On timeout or worker error
 */
export async function executeWorkerTask(workerUrl, message, timeoutMs) {
  const messageId = ++messageIdCounter;
  const startTime = performance.now();

  console.debug(`[WorkerManager] task ${messageId} started (timeout ${timeoutMs}ms)`);

  return new Promise((resolve, reject) => {
    const worker = new Worker(workerUrl);
    let completed = false;
    let timeoutId = null;
    let countdownInterval = null;

    // Cleanup function
    const cleanup = () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (countdownInterval) clearInterval(countdownInterval);
      if (!completed) {
        completed = true;
        worker.terminate();
        console.warn(`[WorkerManager] Worker terminated for task ${messageId}`);
      }
    };

    // Set up timeout with countdown
    // countdown log every second while worker is running
    countdownInterval = setInterval(() => {
      const elapsed = performance.now() - startTime;
      const remaining = Math.max(0, timeoutMs - elapsed);
      console.debug(`[WorkerManager] task ${messageId} time left: ${(remaining / 1000).toFixed(1)}s`);
      if (remaining === 0) {
        clearInterval(countdownInterval);
      }
    }, 1000);

    timeoutId = setTimeout(() => {
      if (completed) return;

      completed = true;
      const elapsed = performance.now() - startTime;
      console.error(`[WorkerManager] timeout after ${timeoutMs}ms (task ${messageId}, took ${elapsed.toFixed(0)}ms)`);

      cleanup();
      reject(new Error(`Worker task timed out after ${timeoutMs}ms`));
    }, timeoutMs);

    // Handle worker messages
    worker.onmessage = (event) => {
      const { id, success, error, ...data } = event.data;

      if (id !== messageId) {
        console.warn(`[WorkerManager] message for other task ignored: ${id} (expected ${messageId})`);
        return;
      }

      if (completed) {
        console.warn(`[WorkerManager] Received message after completion for task ${messageId}`);
        return;
      }

      completed = true;
      const elapsed = performance.now() - startTime;
      cleanup();

      if (error) {
        console.error(`[WorkerManager] task ${messageId} failed after ${elapsed.toFixed(0)}ms:`, error);
        reject(new Error(error));
      } else {
        console.debug(`[WorkerManager] task ${messageId} completed in ${elapsed.toFixed(0)}ms`);
        resolve(data);
      }
    };

    // Handle worker errors
    worker.onerror = (event) => {
      if (completed) return;

      completed = true;
      const elapsed = performance.now() - startTime;
      console.error(`[WorkerManager] worker error (task ${messageId}) after ${elapsed.toFixed(0)}ms:`, event.message);

      cleanup();
      reject(new Error(`Worker error: ${event.message}`));
    };

    // Start the task
    worker.postMessage({
      id: messageId,
      ...message,
    });
  });
}

/**
 * Parse DOCX file in worker with timeout so that it can actually be terminated
 *
 * URL of docxParser.worker.js, DOCX file as ArrayBuffer
 * returns HTML result
 */
export async function parseDocxInWorker(workerUrl, arrayBuffer, timeoutMs) {
  const result = await executeWorkerTask(
    workerUrl,
    {
      type: 'PARSE_DOCX',
      arrayBuffer,
    },
    timeoutMs,
  );

  return { html: result.html, stats: result.stats };
}
